import { useCallback, useEffect, useRef, useState } from "react"

/** Matches the panel's Tailwind `duration-200`. */
export const OPEN_CLOSE_ANIM_MS = 200

export type OpenClosePhase = "closed" | "opening" | "open" | "closing"

function prefersReducedMotion(): boolean {
	return (
		window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
	)
}

/**
 * Four-phase open/close state machine for animated mount/unmount (ZPL-57).
 *
 * "opening" mounts the panel in its hidden state; a double rAF then flips to
 * "open" so the browser paints the hidden frame first and the CSS transition
 * has something to run FROM. "closing" keeps the panel mounted (in its
 * hidden-target state) for the animation, then lands on "closed" — the state
 * that actually unmounts, preserving every unmount-dependent behavior
 * (expand reset, focus-on-open, mount-as-visibility auto-ack).
 *
 * `prefers-reduced-motion: reduce` collapses both directions to an instant
 * toggle; `closeInstantly` is for external suppression (route/menu hiding).
 */
export function useOpenClosePhase(): {
	phase: OpenClosePhase
	open: () => void
	close: () => void
	closeInstantly: () => void
} {
	const [phase, setPhase] = useState<OpenClosePhase>("closed")
	const closeTimer = useRef<number | undefined>(undefined)

	useEffect(() => {
		if (phase !== "opening") return
		// Double rAF: the first fires before the hidden-state paint commits;
		// the second is safely a frame later.
		let second: number
		const first = requestAnimationFrame(() => {
			second = requestAnimationFrame(() => setPhase("open"))
		})
		return () => {
			cancelAnimationFrame(first)
			cancelAnimationFrame(second)
		}
	}, [phase])

	useEffect(
		() => () => {
			window.clearTimeout(closeTimer.current)
		},
		[],
	)

	const open = useCallback(() => {
		window.clearTimeout(closeTimer.current)
		setPhase(prefersReducedMotion() ? "open" : "opening")
	}, [])

	const close = useCallback(() => {
		if (prefersReducedMotion()) {
			setPhase("closed")
			return
		}
		setPhase("closing")
		closeTimer.current = window.setTimeout(
			() => setPhase("closed"),
			OPEN_CLOSE_ANIM_MS,
		)
	}, [])

	const closeInstantly = useCallback(() => {
		window.clearTimeout(closeTimer.current)
		setPhase("closed")
	}, [])

	return { phase, open, close, closeInstantly }
}
